import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import WorkspaceLayout from '@/components/WorkspaceLayout';
import { PARTNER_NAV } from '@/lib/workspaceNav';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, ArrowLeft, CalendarRange, Clock, School, Plus, FileText, ClipboardCheck, Users,
} from 'lucide-react';
import { formatDay } from '@/pages/coordinator/AssignMentorDialog';
import { OfferRow, PROGRAM_STATUS } from './shared';
import PartnerInternsPanel from './PartnerInterns';
import PartnerShortlistPanel from './PartnerShortlist';
import { useOverflowFade } from '@/lib/useOverflowFade';

const TABS = [
  { id: 'offers', label: 'Internship Offers', icon: FileText },
  { id: 'shortlisted', label: 'Shortlisted Students', icon: ClipboardCheck },
  { id: 'interns', label: 'Confirmed Interns', icon: Users },
];

/** One programme: its offers, any student decisions the school has delegated, and the interns. */
export default function PartnerProgramWorkspace() {
  const { programId } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const tabFade = useOverflowFade();

  const tab = TABS.some(t => t.id === params.get('tab')) ? params.get('tab') : 'offers';
  const attendanceOffer = params.get('attendance');
  const feedbackOffer = params.get('feedback');
  const focusDay = params.get('day') || '';
  const focus = useMemo(() => (
    attendanceOffer ? { type: 'attendance', opportunityId: attendanceOffer, day: focusDay }
      : feedbackOffer ? { type: 'feedback', opportunityId: feedbackOffer } : null
  ), [attendanceOffer, feedbackOffer, focusDay]);

  const load = useCallback(async () => {
    try { setData((await api.get('/partner/overview')).data); } catch { setData(null); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const clearFocus = useCallback(() => {
    setParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('attendance');
      next.delete('day');
      next.delete('feedback');
      return next;
    }, { replace: true });
  }, [setParams]);

  const program = (data?.programs || []).find(p => p.id === programId);
  const offers = (data?.offers || []).filter(o => o.program_id === programId);
  const shown = program?.partner_selects ? TABS : TABS.filter(t => t.id !== 'shortlisted');

  if (loading) {
    return (
      <WorkspaceLayout nav={PARTNER_NAV} activeRole="partner" testId="partner-program-workspace">
        <div className="py-20 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[#9CA3AF]" /></div>
      </WorkspaceLayout>
    );
  }

  if (!program) {
    return (
      <WorkspaceLayout nav={PARTNER_NAV} activeRole="partner" testId="partner-program-workspace">
        <p className="py-20 text-center text-sm text-[#9CA3AF]" data-testid="program-not-found">
          This programme is not available to your company.{' '}
          <Link to="/partner/programs" className="text-[#F97D6A] hover:underline">Back to programmes</Link>
        </p>
      </WorkspaceLayout>
    );
  }

  const st = PROGRAM_STATUS[program.status] || PROGRAM_STATUS.draft;

  return (
    <WorkspaceLayout nav={PARTNER_NAV} activeRole="partner" testId="partner-program-workspace">
      <button onClick={() => navigate('/partner/programs')}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#6B7280] hover:text-[#F97D6A] transition-colors mb-3"
        data-testid="back-to-programmes">
        <ArrowLeft className="h-3.5 w-3.5" />Back to Programmes
      </button>

      <div className="mb-5">
        <div className="flex items-center gap-2 mb-1">
          <School className="h-3.5 w-3.5 text-[#F97D6A]" />
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#9CA3AF]" data-testid="program-school-name">{program.school_name}</p>
        </div>
        <div className="flex items-center gap-2.5 flex-wrap">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1F2937] font-['Outfit'] tracking-tight" data-testid="program-title">{program.name}</h1>
          <Badge className={`border-0 text-[10px] ${st.cls}`} data-testid="program-status">{st.label}</Badge>
        </div>
        <p className="text-sm text-[#6B7280] mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="flex items-center gap-1" data-testid="program-dates">
            <CalendarRange className="h-3.5 w-3.5 text-[#9CA3AF]" />{formatDay(program.start_date)} – {formatDay(program.end_date)}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="h-3.5 w-3.5 text-[#9CA3AF]" />Offers by {formatDay(program.application_deadline)}
          </span>
        </p>
      </div>

      <div className={`relative mb-5 ${tabFade.faded ? "after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-10 after:bg-gradient-to-l after:from-[#F8F9FA] after:pointer-events-none" : ''}`}>
      <div ref={tabFade.ref} className="flex gap-1.5 border-b border-[#E5E7EB] overflow-x-auto [&::-webkit-scrollbar]:hidden" data-testid="program-tabs">
        {shown.map(t => (
          <button key={t.id}
            onClick={() => setParams(prev => {
              const next = new URLSearchParams(prev);
              next.set('tab', t.id);
              return next;
            }, { replace: true })}
            className={`px-3.5 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === t.id ? 'border-[#F97D6A] text-[#1F2937]' : 'border-transparent text-[#9CA3AF] hover:text-[#6B7280]'}`}
            data-testid={`program-tab-${t.id}`}>
            <t.icon className="h-3.5 w-3.5 mr-1.5 inline-block" />{t.label}
          </button>
        ))}
      </div>
      </div>

      {tab === 'offers' && (
        <div data-testid="program-offers-tab">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <p className="text-sm text-[#6B7280]">
              {offers.length} offer{offers.length === 1 ? '' : 's'} for this programme.
            </p>
            {program.accepting_offers ? (
              <Link to={`/partner/offers/new?program=${program.id}`}>
                <Button size="sm" className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-lg text-xs" data-testid="program-add-offer">
                  <Plus className="h-3.5 w-3.5 mr-1" />Add an offer
                </Button>
              </Link>
            ) : (
              <span className="text-[11px] text-[#9CA3AF]" data-testid="program-closed-note">
                Closed for new offers — existing records stay read-only
              </span>
            )}
          </div>
          {offers.length === 0 ? (
            <p className="py-16 text-center text-sm text-[#9CA3AF]" data-testid="program-offers-empty">
              No offers yet for this programme.
            </p>
          ) : (
            <div className="space-y-2">{offers.map(o => <OfferRow key={o.id} offer={o} hideProgram readOnly={program.status !== 'open'} />)}</div>
          )}
        </div>
      )}

      {tab === 'shortlisted' && program.partner_selects && (
        <div data-testid="program-shortlisted-tab">
          <p className="text-sm text-[#6B7280] mb-3 max-w-2xl leading-relaxed">
            Students this school has formally proposed for your internships. Accepting confirms you can host them —
            the school still makes the final placement.
          </p>
          <PartnerShortlistPanel programId={programId} />
        </div>
      )}

      {tab === 'interns' && (
        <div data-testid="program-interns-tab">
          <p className="text-sm text-[#6B7280] mb-3 max-w-2xl leading-relaxed">
            {program.status === 'closed'
              ? 'The students this school placed with you, with their attendance and completion notes kept as a record.'
              : 'Students the school has placed with you. Share the reporting details, mark attendance each day, then send your completion note back to the school.'}
          </p>
          <PartnerInternsPanel programId={programId} readOnly={program.status === 'closed'}
            focus={focus} onFocusHandled={clearFocus} />
        </div>
      )}
    </WorkspaceLayout>
  );
}
